import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  PublicPropertiesArg,
  PublicPropertyCounts,
  PublicPropertyDetail,
  PublicPropertyIdArg,
  PublicPropertyListItem,
  PublicPropertyListResult,
  PublicStats
} from './publicTypes';

/**
 * `/api/public/*` — the marketing site and the unauthenticated browse. These routes carry no
 * guard on the backend, but the service still goes through `makeBaseQuery`: a signed-in
 * visitor's token riding along is harmless, and a second `prepareHeaders` is how a slice ends
 * up silently unauthenticated after a token-shape change.
 *
 * The axios version threw `new Error('Failed to load stats')` on a `success: false` body.
 * A normalizer may not throw, and it does not need to: `fail()` on the backend always sets a
 * non-2xx status, so a failure reaches the component as the query's `error`, not as a body.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const envelope = (response: unknown): unknown => {
  const root = asRecord(response);
  return 'data' in root ? root.data : response;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

/** Axios dropped nullish params; `URLSearchParams` would send the string "undefined". */
const toQuery = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export function normalizePublicStats(response: unknown): PublicStats {
  const data = asRecord(envelope(response));
  return {
    totalProperties: asNumber(data.totalProperties),
    verified: asNumber(data.verified),
    deals: asNumber(data.deals),
    lawyers: asNumber(data.lawyers)
  };
}

/** Every value is coerced to a number: the counts feed straight into chart arithmetic. */
export function normalizePropertyCounts(response: unknown): PublicPropertyCounts {
  const data = asRecord(envelope(response));
  const counts: PublicPropertyCounts = {};
  for (const [key, value] of Object.entries(data)) counts[key] = asNumber(value);
  return counts;
}

/**
 * Absorbs three shapes at once: the flat `{ items, total, page, limit, totalPages }` this
 * endpoint actually answers, the `{ items, pagination }` the seller endpoints use, and a
 * bare array. A component that had to tell them apart is a component that will get it wrong.
 */
export function normalizePublicPropertyList(response: unknown): PublicPropertyListResult {
  const data = envelope(response);
  const items = Array.isArray(data)
    ? (data as PublicPropertyListItem[])
    : asArray<PublicPropertyListItem>(asRecord(data).items);
  const raw = asRecord(data);
  const nested = asRecord(raw.pagination);
  const source = Object.keys(nested).length > 0 ? nested : raw;
  const limit = asNumber(source.limit, items.length || 12);
  const total = asNumber(source.total, items.length);
  return {
    items,
    total,
    page: asNumber(source.page, 1),
    limit,
    totalPages: asNumber(source.totalPages, limit > 0 ? Math.ceil(total / limit) : 1)
  };
}

export function normalizePublicPropertyDetail(response: unknown): PublicPropertyDetail | null {
  const data = asRecord(envelope(response));
  return Object.keys(data).length > 0 ? data : null;
}

const uploadBlobs = new Map<string, Blob>();

export const blobForObjectUrl = (objectUrl: string): Blob | undefined => uploadBlobs.get(objectUrl);

export const publicApi = createApi({
  reducerPath: 'publicApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['PublicProperty'],
  endpoints: (builder) => ({
    getPublicStats: builder.query<PublicStats, void>({
      query: () => ({ url: 'public/stats' }),
      transformResponse: (response: unknown) => normalizePublicStats(response),
      providesTags: [{ type: 'PublicProperty', id: 'STATS' }]
    }),
    getPropertyCountsByType: builder.query<PublicPropertyCounts, void>({
      query: () => ({ url: 'public/property-counts-by-type' }),
      transformResponse: (response: unknown) => normalizePropertyCounts(response),
      providesTags: [{ type: 'PublicProperty', id: 'COUNTS' }]
    }),
    listPublicProperties: builder.query<PublicPropertyListResult, PublicPropertiesArg | void>({
      query: (arg) => ({ url: `public/properties${toQuery({ ...(arg ?? {}) })}` }),
      transformResponse: (response: unknown) => normalizePublicPropertyList(response),
      providesTags: (result) => [
        { type: 'PublicProperty' as const, id: 'LIST' },
        ...(result?.items ?? []).map((item) => ({
          type: 'PublicProperty' as const,
          id: String(item.id)
        }))
      ]
    }),
    getPublicPropertyDetail: builder.query<PublicPropertyDetail | null, PublicPropertyIdArg>({
      query: ({ id }) => ({ url: `public/properties/${id}` }),
      transformResponse: (response: unknown) => normalizePublicPropertyDetail(response),
      providesTags: (_result, _error, { id }) => [{ type: 'PublicProperty', id: String(id) }]
    }),
    /**
     * Fire-and-forget view counter. It invalidates nothing on purpose — the axios version
     * swallowed its own rejection, and re-reading the detail just to bump a counter would
     * double every page load.
     */
    recordPublicPropertyView: builder.mutation<void, PublicPropertyIdArg>({
      query: ({ id }) => ({ url: `public/properties/${id}/view`, method: 'POST' })
    }),
    /** helmet's X-Frame-Options blocks embedding an upload cross-origin; a blob: copy is same-origin. */
    getUploadObjectUrl: builder.query<string, { url: string; mime: string }>({
      query: ({ url }) => ({ url, responseHandler: (response) => response.blob() }),
      transformResponse: (blob: Blob, _meta, { mime }) => {
        const copy = new Blob([blob], { type: mime });
        const objectUrl = URL.createObjectURL(copy);
        uploadBlobs.set(objectUrl, copy);
        return objectUrl;
      },
      keepUnusedDataFor: 30,
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        const loaded = await cacheDataLoaded.catch(() => null);
        await cacheEntryRemoved;
        if (!loaded) return;
        URL.revokeObjectURL(loaded.data);
        uploadBlobs.delete(loaded.data);
      }
    })
  })
});

export const {
  useGetPublicStatsQuery,
  useGetPropertyCountsByTypeQuery,
  useListPublicPropertiesQuery,
  useGetPublicPropertyDetailQuery,
  useRecordPublicPropertyViewMutation,
  useGetUploadObjectUrlQuery
} = publicApi;
